<script setup lang="ts">
import { ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
  currentBio: string;
  isSaving?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'save', bio: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const bioText = ref('');
const charCount = ref(0);
const maxChars = 500;

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    bioText.value = props.currentBio;
    charCount.value = props.currentBio.length;
  }
});

watch(bioText, (newVal) => {
  charCount.value = newVal.length;
});

const handleSave = () => {
  if (bioText.value.length <= maxChars) {
    emit('save', bioText.value);
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl my-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-2xl w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-4">
          <h2 class="text-2xl font-bold text-gray-900">Edit Bio</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="p-6">
          <div class="mb-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Tell us about yourself
            </label>
            <textarea
              v-model="bioText"
              :maxlength="maxChars"
              rows="8"
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
              placeholder="Share your interests, goals, and what you're looking to learn or teach..."
            ></textarea>
          </div>
          <div class="flex justify-between items-center text-sm">
            <span class="text-gray-500">
              Character count: 
              <span :class="charCount > maxChars ? 'text-red-500 font-semibold' : 'text-gray-700'">
                {{ charCount }}
              </span> / {{ maxChars }}
            </span>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="charCount > maxChars || isSaving"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md flex items-center gap-2"
          >
            <svg v-if="isSaving" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>

